import { Component, signal, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SortEvent } from 'primeng/api';
import {
  TfDataTableComponent,
  TfColumnDefDirective,
  ColumnDef,
  TfPageEvent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
} from '@tourfold/libraries/shared-ui';
import {
  WebhookDeliveryFailuresApi,
  WebhookDeliveryFailure,
  WebhooksCatalogApi,
  WebhookTypeMetadata,
  WebhookEndpointsApi,
  WebhookEndpoint,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { PlatformI18nService } from '@tourfold/shared-kernels/i18n';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

interface EventTypeOption {
  label: string;
  value: string;
}

interface EndpointOption {
  label: string;
  value: string;
}

@Component({
  selector: 'tf-settings-webhook-failures',
  imports: [
    CommonModule,
    FormsModule,
    TagModule,
    TooltipModule,
    DatePickerModule,
    SelectModule,
    ButtonModule,
    TranslateModule,
    TfDataTableComponent,
    TfColumnDefDirective,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './webhook-failures.html',
  styleUrl: './webhook-failures.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebhookFailures {
  private readonly webhookDeliveryFailuresApi = inject(WebhookDeliveryFailuresApi);
  private readonly webhooksCatalogApi = inject(WebhooksCatalogApi);
  private readonly webhookEndpointsApi = inject(WebhookEndpointsApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly platformI18n = inject(PlatformI18nService);

  readonly columns: ColumnDef[] = [
    {
      field: 'eventType',
      title: 'SETTINGS_WEBHOOKS.FAILURES.EVENT_TYPE',
      type: 'text',
      sortable: true,
      width: '15%',
    },
    {
      field: 'endpointId',
      title: 'SETTINGS_WEBHOOKS.FAILURES.ENDPOINT_ID',
      type: 'text',
      sortable: true,
      width: '20%',
    },
    {
      field: 'attempt',
      title: 'SETTINGS_WEBHOOKS.FAILURES.ATTEMPT',
      type: 'text',
      sortable: true,
      width: '8%',
    },
    {
      field: 'httpStatusCode',
      title: 'SETTINGS_WEBHOOKS.FAILURES.STATUS',
      type: 'custom',
      width: '10%',
    },
    {
      field: 'retriesRemaining',
      title: 'SETTINGS_WEBHOOKS.FAILURES.RETRIES_REMAINING',
      type: 'custom',
      width: '10%',
    },
    {
      field: 'firstAttemptFailedAt',
      title: 'SETTINGS_WEBHOOKS.FAILURES.FIRST_FAILED_AT',
      type: 'custom',
      sortable: true,
      width: '12%',
    },
    {
      field: 'latestRetryAttemptFailedAt',
      title: 'SETTINGS_WEBHOOKS.FAILURES.LATEST_RETRY_FAILED_AT',
      type: 'custom',
      width: '12%',
    },
    {
      field: 'errorMessage',
      title: 'SETTINGS_WEBHOOKS.FAILURES.ERROR_MESSAGE',
      type: 'custom',
      width: '13%',
    },
  ];

  readonly failures = signal<WebhookDeliveryFailure[]>([]);
  readonly isLoading = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(0);
  readonly pageSize = signal(25);

  // Filters
  readonly startDate = signal<Date | null>(null);
  readonly endDate = signal<Date | null>(null);
  readonly eventType = signal<string | null>(null);
  readonly endpointId = signal<string | null>(null);
  readonly eventTypeOptions = signal<EventTypeOption[]>([]);
  readonly endpointOptions = signal<EndpointOption[]>([]);

  // Sorting
  readonly sortField = signal<string>('firstAttemptFailedAt');
  readonly sortOrder = signal<number>(-1); // -1 for desc, 1 for asc

  constructor() {
    this.loadEventTypes();
    this.loadEndpoints();
    this.loadFailures();
  }

  private loadEndpoints(): void {
    from(this.webhookEndpointsApi.listEndpoints())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((response) => response.webhookEndpoints ?? [])
      )
      .subscribe({
        next: (endpoints: WebhookEndpoint[]) => {
          const options: EndpointOption[] = endpoints.map((endpoint) => ({
            label: endpoint.displayName ?? endpoint.endpointUrl ?? endpoint.id,
            value: endpoint.id,
          }));
          this.endpointOptions.set(options);
        },
        error: () => undefined,
      });
  }

  private loadEventTypes(): void {
    from(this.webhooksCatalogApi.getCatalog())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((response) => response.webhooks ?? [])
      )
      .subscribe({
        next: (webhooks: WebhookTypeMetadata[]) => {
          const options: EventTypeOption[] = webhooks.map((metadata) => ({
            label: metadata.displayName ?? metadata.webhookType,
            value: metadata.webhookType,
          }));
          this.eventTypeOptions.set(options);
        },
        error: () => undefined,
      });
  }

  refreshFailures(): void {
    this.loadFailures(0, this.pageSize());
  }

  private loadFailures(page: number = 0, size: number = 25): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);

    // Build sort string (map camelCase field names to snake_case for backend)
    const sortDirection = this.sortOrder() === 1 ? 'asc' : 'desc';
    const backendField = this.mapFieldToBackend(this.sortField());
    const sort = `${backendField},${sortDirection}`;

    from(
      this.webhookDeliveryFailuresApi.listDeliveryFailures({
        page,
        size,
        startDate: this.startDate() ?? undefined,
        endDate: this.endDate() ?? undefined,
        eventType: this.eventType() ?? undefined,
        endpointId: this.endpointId() ?? undefined,
        sort,
      })
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((response) => response)
      )
      .subscribe({
        next: (response) => {
          this.failures.set(response.items ?? []);
          this.totalRecords.set(response.page?.totalElements ?? 0);
          this.currentPage.set(response.page?.number ?? 0);
          this.isLoading.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const message = this.translateService.instant(
            'SETTINGS_WEBHOOKS.FAILURES.ERROR.LOAD_FAILED'
          );
          if (hadLoadedOnce) {
            this.hasLoadedOnce.set(true);
            this.actionError.set(message);
          } else {
            this.loadError.set(message);
            this.initialLoadFailed.set(true);
            void this.updateLoadErrorDetails(error);
          }
        },
      });
  }

  onPageChange(event: TfPageEvent): void {
    const page = event.first / event.rows;
    this.pageSize.set(event.rows);
    this.loadFailures(page, event.rows);
  }

  onSort(event: SortEvent): void {
    this.sortField.set(event.field ?? 'firstAttemptFailedAt');
    this.sortOrder.set(event.order ?? -1);
    this.loadFailures(0, this.pageSize());
  }

  private mapFieldToBackend(field: string): string {
    const fieldMap: Record<string, string> = {
      eventType: 'event_type',
      endpointId: 'endpoint_id',
      attempt: 'attempt',
      firstAttemptFailedAt: 'first_attempt_failed_at',
    };
    return fieldMap[field] ?? field;
  }

  onStartDateChange(date: Date | null): void {
    this.startDate.set(date);
    this.loadFailures(0, this.pageSize());
  }

  onEndDateChange(date: Date | null): void {
    this.endDate.set(date);
    this.loadFailures(0, this.pageSize());
  }

  onEventTypeChange(eventType: string | null): void {
    this.eventType.set(eventType);
    this.loadFailures(0, this.pageSize());
  }

  onEndpointIdChange(endpointId: string | null): void {
    this.endpointId.set(endpointId);
    this.loadFailures(0, this.pageSize());
  }

  clearFilters(): void {
    this.startDate.set(null);
    this.endDate.set(null);
    this.eventType.set(null);
    this.endpointId.set(null);
    this.loadFailures(0, this.pageSize());
  }

  hasActiveFilters(): boolean {
    return (
      this.startDate() !== null ||
      this.endDate() !== null ||
      this.eventType() !== null ||
      this.endpointId() !== null
    );
  }

  getSeverity(failure: WebhookDeliveryFailure): 'danger' | 'warn' | 'info' {
    if (!failure.willRetry) {
      return 'danger';
    }
    if (failure.retriesRemaining <= 2) {
      return 'warn';
    }
    return 'info';
  }

  formatDate(date: Date | undefined): string {
    if (!date) {
      return '-';
    }
    return new Intl.DateTimeFormat(this.platformI18n.resolveFormatLocale(), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(date));
  }

  getHttpStatusSeverity(statusCode: number | undefined): 'danger' | 'warn' | 'info' | 'secondary' {
    if (!statusCode) {
      return 'secondary';
    }
    if (statusCode >= 500) {
      return 'danger';
    }
    if (statusCode >= 400) {
      return 'warn';
    }
    return 'info';
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }
}
